<template>
  <div class="container mt-4">
    <h2>Documentation: BModal Component</h2>

    <p>
      The <code>BModal</code> component is a reusable modal dialog built with
      <strong>Bootstrap 5</strong>. It supports:
      <strong>custom headers</strong>, <strong>dynamic content</strong>,
      <strong>size options</strong>, <strong>full-screen mode</strong>,
      and <strong>events on show/hide</strong>.
    </p>

    <hr />

    <!-- Fullscreen Modal Example -->
    <h3>Example 4: Fullscreen Modal</h3>
    <p>Click the button to open a modal that takes the full screen.</p>
    <button class="btn btn-info mb-2" @click="openFullScreenModal">Open Fullscreen Modal</button>

    <b-modal ref="fullScreenModal" title="Fullscreen Modal" size="sm modal-fullscreen" @shown="modalShown" @hidden="modalHidden">
      <p>This modal takes the full width and height of the viewport.</p>
    </b-modal>

    <pre class="p-2 bg-white text-dark">
      &lt;button class="btn btn-info" @click="openFullScreenModal"&gt;Open Fullscreen Modal&lt;/button&gt;

      &lt;b-modal ref="fullScreenModal" title="Fullscreen Modal" size="sm modal-fullscreen"&gt;
        &lt;p&gt;This modal takes the full width and height of the viewport.&lt;/p&gt;
      &lt;/b-modal&gt;

      methods: {
        openFullScreenModal() {
          this.$refs.fullScreenModal.show();
        }
      }
    </pre>

    <h4>Available Sizes (Bootstrap 5)</h4>
    <ul>
      <li><code>sm</code> — Small modal</li>
      <li><code>lg</code> — Large modal</li>
      <li><code>xl</code> — Extra large modal</li>
      <li><code>modal-fullscreen</code> — Fullscreen modal</li>
      <li><code>modal-fullscreen-sm-down</code> — Fullscreen on small screens and below</li>
      <li><code>modal-fullscreen-md-down</code> — Fullscreen on medium screens and below</li>
      <li><code>modal-fullscreen-lg-down</code> — Fullscreen on large screens and below</li>
      <li><code>modal-fullscreen-xl-down</code> — Fullscreen on extra-large screens and below</li>
      <li><code>modal-fullscreen-xxl-down</code> — Fullscreen on extra-extra-large screens and below</li>
    </ul>

    <!-- Example 1: Basic Modal -->
    <h3>Example 1: Basic Modal</h3>
    <p>Click the button to open a simple modal with default header and body content.</p>
    <button class="btn btn-primary mb-2" @click="openBasicModal">Open Basic Modal</button>
    <b-modal ref="basicModal" title="Hello World" size="lg" @shown="modalShown" @hidden="modalHidden">
      <template #modal-body>
        <p>This is the body content of the modal.</p>
      </template>
    </b-modal>

    <pre class="p-2 bg-white text-dark">
      &lt;button class="btn btn-primary" @click="openBasicModal"&gt;Open Basic Modal&lt;/button&gt;
      &lt;b-modal ref="basicModal" title="Hello World" size="lg"&gt;
        &lt;template #modal-body>
          &lt;p&gt;This is the body content of the modal.&lt;/p&gt;
        &lt;/template>
      &lt;/b-modal&gt;

      methods: {
        openBasicModal() {
          this.$refs.basicModal.show();
        }
      }
    </pre>

    <!-- Example 2: Custom Header -->
    <h3>Example 2: Custom Header</h3>
    <p>This modal uses the <code>modal-header</code> slot to define a fully custom header.</p>
    <button class="btn btn-success mb-2" @click="openCustomHeaderModal">Open Custom Header Modal</button>
    <b-modal ref="customHeaderModal" size="sm">
      <template #modal-header>
        <div class="modal-header">
          <h5 class="modal-title">Custom Header</h5>
          <button type="button" class="btn-close" @click="closeCustomHeaderModal"></button>
        </div>
      </template>
      <template #modal-body>
        <p>This modal has a custom header and a simple body.</p>
      </template>
    </b-modal>

    <pre class="p-2 bg-white text-dark">
      &lt;button class="btn btn-success" @click="openCustomHeaderModal"&gt;Open Custom Header Modal&lt;/button&gt;
      &lt;b-modal ref="customHeaderModal" size="sm"&gt;
        &lt;template #modal-header&gt;
          &lt;div class="modal-header"&gt;
            &lt;h5 class="modal-title"&gt;Custom Header&lt;/h5&gt;
            &lt;button type="button" class="btn-close" @click="closeCustomHeaderModal"&gt;&lt;/button&gt;
          &lt;/div&gt;
        &lt;/template&gt;
        &lt;template #modal-body>
          &lt;p&gt;This modal has a custom header and a simple body.&lt;/p&gt;
        &lt;/template>
      &lt;/b-modal&gt;

      methods: {
        openCustomHeaderModal() {
          this.$refs.customHeaderModal.show();
        },
        closeCustomHeaderModal() {
          this.$refs.customHeaderModal.hide();
        }
      }
    </pre>

    <!-- Example 3: Modal with Form -->
    <h3>Example 3: Modal with Form</h3>
    <p>This modal contains a textarea form and custom footer buttons.</p>
    <button class="btn btn-warning mb-2" @click="openFormModal">Open Form Modal</button>
    <BModal ref="formModal" size="lg">

      <!-- body -->
      <template #modal-body>
        <textarea
            v-model="formData.text"
            class="form-control"
            rows="5"
            placeholder="Enter your text here"
        />
      </template>

      <!-- footer -->
      <template #modal-footer>
        <button class="btn btn-secondary" @click="closeFormModal">
          Cancel
        </button>
        <button class="btn btn-primary" @click="confirmFormModal">
          Submit
        </button>
      </template>

    </BModal>

    <pre class="p-2 bg-white text-dark">
      &lt;button class="btn btn-warning" @click="openFormModal"&gt;Open Form Modal&lt;/button&gt;
      &lt;BModal ref="formModal" size="lg"&gt;
        &lt;!-- body --&gt;
        &lt;template #modal-body>
          &lt;textarea
              v-model="formData.text"
              class="form-control"
              rows="5"
              placeholder="Enter your text here"
          /&gt;
        &lt;/template>

        &lt;!-- footer --&gt;
        &lt;template #modal-footer&gt;
          &lt;button class="btn btn-secondary" @click="closeFormModal"&gt;
            Cancel
          &lt;/button&gt;
          &lt;button class="btn btn-primary" @click="confirmFormModal"&gt;
            Submit
          &lt;/button&gt;
        &lt;/template&gt;
      &lt;/BModal&gt;

      methods: {
        openFormModal() {
          this.$refs.formModal.show();
        },
        closeFormModal() {
          this.$refs.formModal.hide();
        },
        confirmFormModal() {
          console.log('Form submitted:', this.formData.text);
          this.$refs.formModal.hide();
        }
      }
    </pre>

    <hr />

    <h2>Documentation: MessageBox Component</h2>

    <p>
      The <code>MessageBox</code> component is a reusable modal for displaying messages,
      confirmations, or prompts. It supports:
      <strong>custom buttons</strong>, <strong>dynamic messages</strong>,
      <strong>slot content</strong>, and <strong>events</strong>.
    </p>

    <h3>Example 1: Basic MessageBox</h3>
    <p>Open a simple confirmation modal with default buttons.</p>
    <button class="btn btn-primary mb-2" @click="openBasicMessageBox">Open MessageBox</button>
    <MessageBox ref="basicMessageBox" @confirm="onConfirm" @hidden="onHidden" />

    <pre class="p-2 bg-white text-dark">
      &lt;button class="btn btn-primary" @click="openBasicMessageBox"&gt;Open MessageBox&lt;/button&gt;
      &lt;MessageBox ref="basicMessageBox" @confirm="onConfirm" @hidden="onHidden" /&gt;

      methods: {
        openBasicMessageBox() {
          this.$refs.basicMessageBox.show(
            { id: 1 },
            'Confirmation',
            'Are you sure you want to proceed?'
          );
        },
        onConfirm(data) {
          console.log('Confirmed data:', data);
        },
        onHidden() {
          console.log('MessageBox closed');
        }
      }
    </pre>

    <h3>Example 2: Custom Buttons</h3>
    <p>MessageBox with only a confirm button and custom text labels.</p>
    <button class="btn btn-success mb-2" @click="openCustomBtnBox">Open Custom Button Box</button>
    <MessageBox ref="customBtnBox" :btns="['confirm']" @confirm="onConfirmCustom" />

    <pre class="p-2 bg-white text-dark">
      &lt;button class="btn btn-success" @click="openCustomBtnBox"&gt;Open Custom Button Box&lt;/button&gt;
      &lt;MessageBox ref="customBtnBox" :btns="['confirm']" @confirm="onConfirmCustom" /&gt;

      methods: {
        openCustomBtnBox() {
          this.$refs.customBtnBox.show(
            { id: 42 },
            'Custom Confirm',
            'This box has only a confirm button',
            'Cancel',
            'OK'
          );
        },
        onConfirmCustom(data) {
          console.log('Confirmed custom data:', data);
        }
      }
    </pre>

    <h3>Example 3: Slot Message</h3>
    <p>MessageBox using a custom slot to render message content dynamically.</p>
    <button class="btn btn-warning mb-2" @click="openSlotBox">Open Slot Box</button>
    <MessageBox ref="slotBox">
      <template #message="{ data, message, title }">
        <strong>Custom Slot Content:</strong> {{ message }} (ID: {{ data?.id ?? 'N/A' }})
      </template>
    </MessageBox>

    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;MessageBox ref="slotBox"&gt;
        &lt;template #message="{ data, message, title }"&gt;
      &lt;strong&gt;Custom Slot Content:&lt;/strong&gt; <span v-pre>{{ message }}</span> (ID: <span v-pre>{{ data?.id ?? 'N/A' }}</span>)
        &lt;/template&gt;
      &lt;/MessageBox&gt;

      methods: {
        openSlotBox() {
          this.$refs.slotBox.show({ id: 99 }, 'Slot Example', 'This message is rendered via slot');
        }
      }
    </code></pre>
  </div>
</template>

<script>
import MessageBox from "@/components/common/messagebox/MessageBox.vue";

export default {
  components: { MessageBox },
  data() {
    return {
      formData: { text: '' },
    };
  },
  methods: {
    // BModal methods
    openFullScreenModal() { this.$refs.fullScreenModal.show(); },
    openBasicModal() { this.$refs.basicModal?.show(); },
    openCustomHeaderModal() { this.$refs.customHeaderModal?.show(); },
    closeCustomHeaderModal() { this.$refs.customHeaderModal?.hide(); },
    openFormModal() { this.$refs.formModal?.show(); },
    closeFormModal() { this.$refs.formModal?.hide(); },
    confirmFormModal() {
      console.log('Form submitted:', this.formData.text);
      this.$refs.formModal?.hide();
    },
    modalShown() { console.log('Modal is shown'); },
    modalHidden() { console.log('Modal is hidden'); },

    // MessageBox methods
    openBasicMessageBox() {
      this.$refs.basicMessageBox?.show({ id: 1 }, 'Confirmation', 'Are you sure you want to proceed?');
    },
    openCustomBtnBox() {
      this.$refs.customBtnBox?.show({ id: 42 }, 'Custom Confirm', 'This box has only a confirm button', 'Cancel', 'OK');
    },
    openSlotBox() {
      this.$refs.slotBox?.show({ id: 99 }, 'Slot Example', 'This message is rendered via slot');
    },
    onConfirm(data) { console.log('Confirmed data:', data); },
    onConfirmCustom(data) { console.log('Confirmed custom data:', data); },
    onHidden() { console.log('MessageBox closed'); },
  }
};
</script>